// covered-by: frontends/carrie/src/ask-v2/transport/__tests__/applyStreamSnapshot.test.ts
import { type StreamingState } from "../../hooks/useAskPunttStream";

/**
 * The publisher's own "I cut this answer" stamp, or null when the frame carries
 * none.
 *
 * The snapshot is bounded as one document and the answer is the field it gives
 * up LAST, by halving it
 * (`services/warhol/runtime/clients/ask_puntt_stream.py::fit_snapshot`,
 * `warhol/specs/roberto.faga/0040`). The publisher is the half that KNOWS it
 * did that, and `streamingContentTruncated` is where it says so, which is the
 * whole reason the field exists: a budget cut and the model REWRITING its
 * sentence both arrive as a shorter string, and the shape of the text cannot
 * tell them apart — a rewrite that re-opens with the same words is a strict
 * prefix of what is on screen too. Laid down, a cut makes a paragraph shrink
 * under the person reading it and the next unfitted frame grows it back; held,
 * a rewrite freezes the old paragraph on screen. Only the stamp picks.
 *
 * `null`, not `false`, for a frame that carries no stamp: rembrandt's snapshots
 * never had one, and neither does a warhol worker still in flight across the
 * deploy that writes it. Those fall back to `prefixOfHeldText`. A stamped
 * `false` is an answer, not a gap — lay that frame down even when it is a
 * prefix.
 *
 * No producer writes `false` today: `fit_snapshot` stamps `True` on the write
 * it cut and pops the field otherwise
 * (`services/warhol/runtime/clients/ask_puntt_stream.py`), so a live frame is
 * only ever stamped `true` or unstamped. The `false` arm is kept for a future
 * writer that says "not a cut" out loud — a resumed turn or a rewrite the
 * publisher can name — so that writer lands against a stated precedence
 * instead of having to add one.
 */
export function fitCutStamp(snapshot: StreamingState): boolean | null {
  const stamped = snapshot.streamingContentTruncated;
  return typeof stamped === "boolean" ? stamped : null;
}

/**
 * Whether an UNSTAMPED shorter frame has the shape of a cut: the text already
 * on screen with its tail missing.
 *
 * The fallback for the frames `fitCutStamp` cannot answer for, and a guess, not
 * a reading — it is exactly the case the stamp was added to settle. It holds a
 * genuine rewrite that happens to re-open with the same words, and that is the
 * price of a frame that says nothing about itself; it is still better than
 * laying every halved answer straight down.
 *
 * Shorter ALONE is not even the shape. warhol's reducer resets its text on
 * every new part (`runtime/live_steps.py::content`), so the opening frames of a
 * new part are short and non-empty by construction. Held on length alone, the
 * PREVIOUS part would stay on screen until the new one outgrew it.
 */
export function prefixOfHeldText(next: string, held: string | null): boolean {
  return held !== null && next.length < held.length && held.startsWith(next);
}

/**
 * Whether this frame's answer is the size fit's cut of the text on screen.
 *
 * The stamp when the frame carries one, its shape when it does not. One
 * function so the precedence is stated once: a reader that consulted the shape
 * first would overrule the publisher on the very frames the publisher is
 * telling it about.
 */
export function fitCutThisFrame(
  snapshot: StreamingState,
  next: string,
  held: string | null,
): boolean {
  return fitCutStamp(snapshot) ?? prefixOfHeldText(next, held);
}
